import { computed, ComputedRef } from 'vue';
import { DailyRecord } from '@/types/sector';
import {
  buildCumulativeSeries,
  filterRecentMonths,
  sortByDate,
  topNSectors,
  weeklyGrowth,
} from '@/utils/score';

export interface ChartData {
  dates: string[];
  topSectors: string[];
  topSectorsByGrowth: string[];
  cumulative: Record<string, number[]>;
  growth: Record<string, number>;
  highestGrowth: { sector: string | null; value: number };
  topPie: { sectors: string[]; scores: number[] };
  totalDays: number;
  uniqueSectors: number;
  mostActive: { name: string; score: number } | null;
}

export function useSectorStats(
  dailyData: ComputedRef<DailyRecord[]>,
  sectorScores: ComputedRef<Record<string, number>>,
): ComputedRef<ChartData> {
  return computed<ChartData>(() => {
    const data = dailyData.value;
    const scores = sectorScores.value;
    const sortedAsc = sortByDate(data);
    const recent = filterRecentMonths(sortedAsc, 2);

    // Top 5 板块（近 2 月）
    const recentScores: Record<string, number> = {};
    for (const r of recent) {
      recentScores[r.first] = (recentScores[r.first] ?? 0) + 50;
      recentScores[r.second] = (recentScores[r.second] ?? 0) + 30;
      recentScores[r.third] = (recentScores[r.third] ?? 0) + 20;
    }
    const top5 = topNSectors(recentScores, 5);
    const cumulative = buildCumulativeSeries(recent, top5);
    const growth = weeklyGrowth(cumulative, 7);

    // 按周增长排序
    const top5ByGrowth = [...top5].sort(
      (a, b) => (growth[b] ?? 0) - (growth[a] ?? 0),
    );

    // 增长冠军
    let highestSector: string | null = null;
    let highestValue = -Infinity;
    for (const [s, v] of Object.entries(growth)) {
      if (v > highestValue) {
        highestValue = v;
        highestSector = s;
      }
    }

    // 饼图：近 2 月 top 5
    const topPie = {
      sectors: top5,
      scores: top5.map((s) => recentScores[s] ?? 0),
    };

    // 总体统计
    const uniqueSectors = Object.keys(scores).length;
    const sortedScores = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    const mostActive =
      sortedScores.length > 0
        ? { name: sortedScores[0][0], score: sortedScores[0][1] }
        : null;

    return {
      dates: recent.map((r) => r.date),
      topSectors: top5,
      topSectorsByGrowth: top5ByGrowth,
      cumulative,
      growth,
      highestGrowth: { sector: highestSector, value: highestValue },
      topPie,
      totalDays: data.length,
      uniqueSectors,
      mostActive,
    };
  });
}
